/**
 * Amortización método flat:
 * el interés por cuota se calcula sobre el capital inicial
 * y se mantiene constante en todas las cuotas.
 *
 * En esta versión:
 * - SIEMPRE se usa el BALANCE PENDIENTE del archivo
 * - desde ahí se calcula cuánto se ha pagado
 * - luego se distribuye cuota por cuota
 * - prioridad en cuota parcial: interés primero, luego capital
 */


function parseNumero(val) {
  if (val == null || val === "") return null;
  let t = String(val).trim().replace(/\s/g, "");
  if (t === "") return null;

  const hasComma = t.includes(",");
  const hasDot = t.includes(".");

  if (/^\d{1,3}(\.\d{3})+,\d{1,4}$/.test(t)) {
    t = t.replace(/\./g, "").replace(",", ".");
  } else if (hasComma && !hasDot && /^\d+,\d{1,2}$/.test(t)) {
    t = t.replace(",", ".");
  } else {
    t = t.replace(/,/g, "");
  }

  const n = Number(t);
  return Number.isFinite(n) ? n : null;
}

function getCampo(row, ...nombres) {
  const keys = Object.keys(row).filter((k) => k !== "id");
  for (const nombre of nombres) {
    const want = nombre.trim().toLowerCase();
    const found = keys.find((k) => k.trim().toLowerCase() === want);
    if (found !== undefined) return row[found];
  }
  return undefined;
}

/** Cédula, RNC o pasaporte: comparten la columna cedula. En la importación
 *  no se valida el largo ni el formato, solo que venga lleno. */
function documentoIdentidadValido(value) {
  return String(value ?? "").trim() !== "";
}

function frecuenciaRawValida(value) {
  return ["Mensual", "Dias", "Quincenas", "Semanas"].includes(String(value ?? "").trim());
}

/** Columnas opcionales del garante (fiador de `solicit`). Van todas o
 *  ninguna: un préstamo con garante a medias no se importa. */
export const GARANTE_COLUMNAS = [
  "garante",
  "cedula garante",
  "direccion garante",
  "telefono garante",
];

/** Lee las cuatro celdas del garante de la fila (vacías si no vienen). */
export function leerGaranteRow(row) {
  const texto = (v) => String(v ?? "").trim();
  return {
    nombre: texto(getCampo(row, "garante", "fiador", "nombre garante")),
    cedula: texto(getCampo(row, "cedula garante", "cédula garante", "cedula fiador")),
    direccion: texto(getCampo(row, "direccion garante", "dirección garante", "direccion fiador")),
    telefono: texto(getCampo(row, "telefono garante", "teléfono garante", "telefono fiador")),
  };
}

/** Todo o nada: sin ninguna celda del garante la fila es válida; con alguna
 *  deben venir las cuatro y el documento debe ser cédula/RNC/pasaporte. */
export function validarGaranteRow(row) {
  const g = leerGaranteRow(row);
  const valores = [g.nombre, g.cedula, g.direccion, g.telefono];
  const llenas = valores.filter((v) => v !== "").length;
  if (llenas === 0) return { ok: true, tiene: false, garante: g, error: null };
  if (llenas < valores.length) {
    return {
      ok: false,
      tiene: true,
      garante: g,
      error: {
        field: "garante",
        message:
          "Datos del garante incompletos: si indica un garante debe llenar garante, cedula garante, direccion garante y telefono garante.",
      },
    };
  }
  if (!documentoIdentidadValido(g.cedula)) {
    return {
      ok: false,
      tiene: true,
      garante: g,
      error: {
        field: "cedula garante",
        message:
          "Falta el documento del garante (cédula, RNC o pasaporte).",
      },
    };
  }
  return { ok: true, tiene: true, garante: g, error: null };
}

/** Columnas de la matrícula financiada en las primeras cuotas. Solo van en la
 *  plantilla cuando en la pantalla se marca "Matrícula incluida". */
export const MATRICULA_COLUMNAS = ["matricula", "cuotas matricula"];
export const MATRICULA_CUOTAS_DEFECTO = 4;

/** Matrícula de la fila: valor total (sin interés) y en cuántas cuotas se
 *  reparte desde la cuota 1. Celda de cuotas vacía = MATRICULA_CUOTAS_DEFECTO. */
export function leerMatriculaRow(row) {
  const valorRaw = getCampo(row, "matricula", "matrícula");
  const cuotasRaw = getCampo(row, "cuotas matricula", "cuotas matrícula");
  const vacio = (v) => v == null || String(v).trim() === "";
  return {
    valor: vacio(valorRaw) ? 0 : parseNumero(valorRaw),
    cuotas: vacio(cuotasRaw) ? MATRICULA_CUOTAS_DEFECTO : parseNumero(cuotasRaw),
  };
}

/** Valida la matrícula contra el plazo. Valor vacío o 0 = préstamo sin
 *  matrícula financiada. */
function validarMatriculaRow(row, plazo) {
  const { valor, cuotas } = leerMatriculaRow(row);
  if (valor == null || valor < 0) {
    return { error: { field: "matricula", message: "Matrícula debe ser un número mayor o igual a cero." } };
  }
  if (!valor) return { matricula: 0, cuotasMatricula: 0 };
  if (cuotas == null || !Number.isInteger(cuotas) || cuotas < 1) {
    return {
      error: { field: "cuotas matricula", message: "Cuotas matrícula debe ser un número entero mayor que cero." },
    };
  }
  if (plazo != null && cuotas > plazo) {
    return {
      error: {
        field: "cuotas matricula",
        message: `Cuotas matrícula (${cuotas}) no puede ser mayor que el plazo (${plazo}).`,
      },
    };
  }
  return { matricula: Number(valor.toFixed(2)), cuotasMatricula: cuotas };
}

/** `opciones.matricula`: la pantalla marcó "Matrícula incluida" y la fila
 *  puede traer las columnas de MATRICULA_COLUMNAS. Sin la marca se ignoran. */
export function validarPrestamoRow(row, opciones = {}) {
  const errors = [];
  const cedula = String(getCampo(row, "cedula", "Cedula", "Cédula") ?? "").trim();
  const capital = parseNumero(getCampo(row, "Capital", "capital"));
  const tasa = parseNumero(getCampo(row, "tasa", "Tasa"));
  const plazo = parseNumero(getCampo(row, "plazo", "Plazo"));
  const frecuenciaRaw = String(getCampo(row, "frecuencia", "Frecuencia") ?? "").trim();
  const frecuencia = parseFrecuencia(frecuenciaRaw);
  const fechaInicio = parseFechaLoose(getCampo(row, "fecha de inicio", "fecha de inicio "));
  const balancePendiente = parseNumero(
    getCampo(row, "balance pendiente", "balancependiente", "saldo pendiente", "saldo_pendiente")
  );

  if (capital == null || capital <= 0) {
    errors.push({ field: "capital", message: "Capital debe ser un número mayor que cero." });
  }

  if (tasa == null || tasa < 0) {
    errors.push({ field: "tasa", message: "Tasa debe ser un número mayor o igual a cero." });
  }

  const plazoEntero = plazo != null && plazo > 0 ? Math.max(1, Math.round(plazo)) : null;
  if (plazoEntero == null) {
    errors.push({ field: "plazo", message: "Plazo debe ser un número positivo (número de cuotas)." });
  }

  if (!frecuenciaRawValida(frecuenciaRaw) || frecuencia == null || frecuencia <= 0) {
    errors.push({
      field: "frecuencia",
      message: "Frecuencia no válida. Debe ser exactamente una de estas: Mensual, Dias, Quincenas, Semanas.",
    });
  }

  if (!documentoIdentidadValido(cedula)) {
    errors.push({
      field: "cedula",
      message:
        "Falta el documento (cédula, RNC o pasaporte).",
    });
  }

  if (!fechaInicio) {
    errors.push({ field: "fecha de inicio", message: "Fecha de inicio no válida o vacía." });
  }

  if (balancePendiente == null || balancePendiente < 0) {
    errors.push({ field: "balance pendiente", message: "Balance pendiente no válido o vacío." });
  }

  const garante = validarGaranteRow(row);
  if (!garante.ok) {
    errors.push(garante.error);
  }

  let matricula = 0;
  let cuotasMatricula = 0;
  if (opciones.matricula) {
    const m = validarMatriculaRow(row, plazoEntero);
    if (m.error) errors.push(m.error);
    else ({ matricula, cuotasMatricula } = m);
    // Cuota fija: la porción de matrícula sale del capital de la cuota y no
    // puede ser mayor que él (el vehículo quedaría negativo).
    if (!m.error && matricula > 0 && opciones.matriculaCuotaFija && capital != null && plazoEntero) {
      const capitalCuota = (capital + matricula) / plazoEntero;
      if (matricula / cuotasMatricula > capitalCuota + 0.005) {
        errors.push({
          field: "cuotas matricula",
          message: `Con cuota fija la matrícula por cuota (${(matricula / cuotasMatricula).toFixed(2)}) no puede ser mayor que el capital de la cuota (${capitalCuota.toFixed(2)}). Reparta la matrícula en más cuotas.`,
        });
      }
    }
  }

  if (errors.length > 0) {
    return { ok: false, normalizedData: null, errors };
  }

  return {
    ok: true,
    normalizedData: {
      cedula,
      capital,
      tasa,
      plazo: plazoEntero,
      frecuenciaRaw,
      frecuencia,
      fechaInicio,
      balancePendiente,
      matricula,
      cuotasMatricula,
    },
    errors: [],
  };
}

export function parseFrecuencia(val) {
  if (val == null || val === "") return null;
  const s = String(val).trim();
  const map = {
    Mensual: 12,
    Dias: 365,
    Quincenas: 24,
    Semanas: 52,
  };
  if (map[s] != null) return map[s];
  return null;
}

function makeLocalDate(year, month, day) {
  const y = parseInt(year, 10);
  const m = parseInt(month, 10);
  const d = parseInt(day, 10);
  if (
    !Number.isFinite(y) ||
    !Number.isFinite(m) ||
    !Number.isFinite(d) ||
    m < 1 ||
    m > 12 ||
    d < 1 ||
    d > 31
  ) {
    return null;
  }
  const date = new Date(y, m - 1, d);
  // Rechaza fechas imposibles tipo 31/02 (JS las "rebalancea" silenciosamente).
  if (
    date.getFullYear() !== y ||
    date.getMonth() !== m - 1 ||
    date.getDate() !== d
  ) {
    return null;
  }
  return date;
}

export function parseFechaLoose(val) {
  if (val == null || val === "") return null;

  if (val instanceof Date) {
    if (Number.isNaN(val.getTime())) return null;
    return new Date(val.getFullYear(), val.getMonth(), val.getDate());
  }

  if (typeof val === "number" && Number.isFinite(val)) {
    // Excel epoch (1899-12-30) en UTC para evitar que DST/zona horaria
    // muevan el día al convertir.
    const ms = Date.UTC(1899, 11, 30) + Math.floor(val) * 86400000;
    const d = new Date(ms);
    if (Number.isNaN(d.getTime())) return null;
    return new Date(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
  }

  let s = String(val).trim();
  if (s === "") return null;

  if (s.includes("T")) s = s.split("T")[0];
  if (s.includes(" ")) s = s.split(" ")[0];

  // ISO: YYYY-MM-DD o YYYY/MM/DD
  let m = s.match(/^(\d{4})[-/.](\d{1,2})[-/.](\d{1,2})$/);
  if (m) return makeLocalDate(m[1], m[2], m[3]);

  // DD/MM/YYYY, D/M/YY, DD-MM-YYYY, DD.MM.YYYY
  m = s.match(/^(\d{1,2})[/\-.](\d{1,2})[/\-.](\d{2}|\d{4})$/);
  if (m) {
    let year = m[3];
    if (year.length === 2) {
      const yi = parseInt(year, 10);
      year = String(yi >= 70 ? 1900 + yi : 2000 + yi);
    }
    return makeLocalDate(year, m[2], m[1]);
  }

  // Serial de Excel pasado como string ("45658" o "45658.5")
  if (/^\d+(\.\d+)?$/.test(s)) {
    return parseFechaLoose(parseFloat(s));
  }

  return null;
}

function addMonths(date, months) {
  const d = new Date(date.getTime());
  d.setMonth(d.getMonth() + months);
  return d;
}

function addDays(date, days) {
  const d = new Date(date.getTime());
  d.setDate(d.getDate() + days);
  return d;
}

function sumarSegunFrecuencia(fechaInicio, periodo, frecuencia) {
  if (frecuencia === 12) return addMonths(fechaInicio, periodo);
  if (frecuencia === 24) return addDays(fechaInicio, periodo * 15);
  if (frecuencia === 52) return addDays(fechaInicio, periodo * 7);
  if (frecuencia === 6) return addMonths(fechaInicio, periodo * 2);
  if (frecuencia === 4) return addMonths(fechaInicio, periodo * 3);
  if (frecuencia === 2) return addMonths(fechaInicio, periodo * 6);
  if (frecuencia === 1) return addMonths(fechaInicio, periodo * 12);

  const diasPorPeriodo = Math.round(365 / frecuencia);
  return addDays(fechaInicio, periodo * diasPorPeriodo);
}

/**
 * Capital programado de cada cuota.
 *
 * Modo normal: el del vehículo en partes iguales (la última absorbe el
 * redondeo) más la porción de matrícula de las primeras `cuotasMatricula`
 * cuotas (la última de ellas absorbe su redondeo, igual que la distribución
 * de un cobro financiado en el backend). Esas cuotas salen más altas.
 *
 * `cuotaFija`: vehículo + matrícula en partes iguales en todo el plazo, así la
 * cuota no cambia. En las primeras cuotas la porción de matrícula sale del
 * capital del vehículo, que se compensa en las cuotas restantes.
 */
function programarCapital({ capital, plazo, capitalPorCuotaBase, matricula, cuotasMatricula, cuotaFija }) {
  const porcion = cuotasMatricula > 0 ? Number((matricula / cuotasMatricula).toFixed(2)) : 0;
  const total = Number((capital + matricula).toFixed(2));
  const baseFija = Number((total / plazo).toFixed(2));
  const cuotas = [];
  let capitalAcumulado = 0;
  for (let k = 1; k <= plazo; k += 1) {
    let mat = 0;
    if (k < cuotasMatricula) mat = porcion;
    else if (k === cuotasMatricula) mat = Number((matricula - porcion * (cuotasMatricula - 1)).toFixed(2));
    if (cuotaFija) {
      const cap = k < plazo ? baseFija : Number((total - capitalAcumulado).toFixed(2));
      capitalAcumulado = Number((capitalAcumulado + cap).toFixed(2));
      cuotas.push({ capital: cap, matricula: mat });
    } else {
      const vehiculo =
        k < plazo
          ? Number(capitalPorCuotaBase.toFixed(2))
          : Number((capital - capitalAcumulado).toFixed(2));
      capitalAcumulado = Number((capitalAcumulado + vehiculo).toFixed(2));
      cuotas.push({ capital: Number((vehiculo + mat).toFixed(2)), matricula: mat });
    }
  }
  return cuotas;
}

/**
 * Calcula capital pagado e interés pagado usando SIEMPRE el balance pendiente.
 * Regla:
 * - totalPagado = totalPrestamo - balancePendiente
 * - se aplica cuota por cuota
 * - en cuota parcial: interés primero, luego capital
 * La matrícula va dentro del capital de sus cuotas y no paga interés.
 */
function calcularPagadoDesdeBalancePendiente({
  capital,
  plazo,
  balancePendiente,
  interesPorCuota,
  programa,
  matricula,
}) {
  const interesTotal = Number((interesPorCuota * plazo).toFixed(2));
  const totalPrestamo = Number((capital + matricula + interesTotal).toFixed(2));

  const balancePendienteSeguro = Number(
    Math.max(0, Math.min(balancePendiente ?? totalPrestamo, totalPrestamo)).toFixed(2)
  );

  let totalPagado = Number(
    (totalPrestamo - balancePendienteSeguro).toFixed(2)
  );

  let capitalPagado = 0;
  let interesPagado = 0;

  for (let k = 1; k <= plazo; k += 1) {
    const capitalProgramado = programa[k - 1].capital;
    const interesProgramado = Number(interesPorCuota.toFixed(2));
    const cuotaProgramada = Number(
      (capitalProgramado + interesProgramado).toFixed(2)
    );

    if (totalPagado <= 0) break;

    if (totalPagado >= cuotaProgramada) {
      interesPagado = Number((interesPagado + interesProgramado).toFixed(2));
      capitalPagado = Number((capitalPagado + capitalProgramado).toFixed(2));
      totalPagado = Number((totalPagado - cuotaProgramada).toFixed(2));
    } else {
      // cuota parcial: primero interés
      const interesAplicado = Number(
        Math.min(totalPagado, interesProgramado).toFixed(2)
      );
      interesPagado = Number((interesPagado + interesAplicado).toFixed(2));
      totalPagado = Number((totalPagado - interesAplicado).toFixed(2));

      if (totalPagado > 0) {
        const capitalAplicado = Number(
          Math.min(totalPagado, capitalProgramado).toFixed(2)
        );
        capitalPagado = Number((capitalPagado + capitalAplicado).toFixed(2));
        totalPagado = Number((totalPagado - capitalAplicado).toFixed(2));
      }

      break;
    }
  }

  return {
    capitalPagadoImporte: Number(capitalPagado.toFixed(2)),
    interesPagadoImporte: Number(interesPagado.toFixed(2)),
    interesTotal,
    totalPrestamo,
    balancePendiente: balancePendienteSeguro,
  };
}

export function extraerParametrosPrestamo(row, opciones = {}) {
  const validation = validarPrestamoRow(row, opciones);
  if (!validation.ok) {
    return { ok: false, error: validation.errors[0]?.message || "Datos del préstamo no válidos." };
  }
  const {
    capital,
    tasa,
    plazo: plazoEntero,
    frecuenciaRaw,
    frecuencia,
    fechaInicio,
    balancePendiente,
    matricula,
    cuotasMatricula,
  } = validation.normalizedData;

  // Con cuota fija el capital de cada cuota incluye la matrícula repartida
  // en todo el plazo (ver programarCapital).
  const capitalPorCuotaBase = opciones.matriculaCuotaFija
    ? (capital + matricula) / plazoEntero
    : capital / plazoEntero;

  // Se asume tasa flat por cuota/período
  var tasaFinal = tasa;
  if(frecuenciaRaw == "Dias") {
    tasaFinal = tasa / 30;
  } else if(frecuenciaRaw == "Quincenas") {
    tasaFinal = tasa / 2;
  } 
  else if(frecuenciaRaw == "Semanas") {
    tasaFinal = tasa / 4;
  }
  const interesPorCuota = capital * (tasaFinal / 100);
  const cuotaBase = capitalPorCuotaBase + interesPorCuota;

  const programa = programarCapital({
    capital,
    plazo: plazoEntero,
    capitalPorCuotaBase,
    matricula,
    cuotasMatricula,
    cuotaFija: !!opciones.matriculaCuotaFija,
  });

  const calculado = calcularPagadoDesdeBalancePendiente({
    capital,
    plazo: plazoEntero,
    balancePendiente,
    interesPorCuota,
    programa,
    matricula,
  });

  return {
    ok: true,
    params: {
      capital,
      tasa,
      plazo: plazoEntero,
      frecuencia,
      fechaInicio,
      cuotaBase,
      capitalPorCuotaBase,
      interesPorCuota,
      matricula,
      cuotasMatricula,
      programa,
      interesTotal: calculado.interesTotal,
      capitalPagadoImporte: calculado.capitalPagadoImporte,
      interesPagadoImporte: calculado.interesPagadoImporte,
      balancePendienteOriginal: calculado.balancePendiente,
      totalPrestamo: calculado.totalPrestamo,
    },
  };
}

export function generarTablaAmortizacionFlat(row, opciones = {}) {
  const ext = extraerParametrosPrestamo(row, opciones);
  if (!ext.ok) return ext;

  const {
    capital,
    plazo,
    frecuencia,
    fechaInicio,
    cuotaBase,
    interesPorCuota,
    matricula,
    cuotasMatricula,
    programa,
    interesTotal,
    capitalPagadoImporte,
    interesPagadoImporte,
    balancePendienteOriginal,
    totalPrestamo,
  } = ext.params;

  const filas = [];
  const capitalTotal = Number((capital + matricula).toFixed(2));

  let capitalRestantePorAplicar = Math.min(capitalPagadoImporte, capitalTotal);
  let interesRestantePorAplicar = Math.min(interesPagadoImporte, interesTotal);

  let capitalAplicadoAcumulado = 0;

  for (let k = 1; k <= plazo; k += 1) {
    const fecha = sumarSegunFrecuencia(fechaInicio, k, frecuencia);

    const capitalProgramado = programa[k - 1].capital;
    const matriculaCuota = programa[k - 1].matricula;

    const interesProgramado = Number(interesPorCuota.toFixed(2));
    const cuotaProgramada = Number(
      (capitalProgramado + interesProgramado).toFixed(2)
    );

    const capitalAplicado = Number(
      Math.min(capitalRestantePorAplicar, capitalProgramado).toFixed(2)
    );
    capitalRestantePorAplicar = Number(
      (capitalRestantePorAplicar - capitalAplicado).toFixed(2)
    );

    const interesAplicado = Number(
      Math.min(interesRestantePorAplicar, interesProgramado).toFixed(2)
    );
    interesRestantePorAplicar = Number(
      (interesRestantePorAplicar - interesAplicado).toFixed(2)
    );

    const capitalPendiente = Number(
      (capitalProgramado - capitalAplicado).toFixed(2)
    );
    const interesPendiente = Number(
      (interesProgramado - interesAplicado).toFixed(2)
    );
    const totalPagado = Number(
      (capitalAplicado + interesAplicado).toFixed(2)
    );

    let estado = "pendiente";
    if (capitalPendiente === 0 && interesPendiente === 0) {
      estado = "pagada";
    } else if (capitalAplicado > 0 || interesAplicado > 0) {
      estado = "parcial";
    }

    capitalAplicadoAcumulado = Number(
      (capitalAplicadoAcumulado + capitalAplicado).toFixed(2)
    );

    const saldo = Number(
      Math.max(0, capitalTotal - capitalAplicadoAcumulado).toFixed(2)
    );

    // Dentro del capital de la cuota la matrícula se salda primero (misma
    // regla que el libro de cargos financiados al cobrar).
    const matriculaPagada = Number(Math.min(capitalAplicado, matriculaCuota).toFixed(2));

    filas.push({
      periodo: k,
      fecha,
      cuota: cuotaProgramada,
      capital: capitalProgramado,
      interes: interesProgramado,
      capitalPagado: capitalAplicado,
      interesPagado: interesAplicado,
      totalPagado,
      capitalPendiente,
      interesPendiente,
      saldo,
      estado,
      matricula: matriculaCuota,
      matriculaPagada,
    });
  }

  const cuotasPagadasCompletas = filas.filter((f) => f.estado === "pagada").length;
  const cuotasParciales = filas.filter((f) => f.estado === "parcial").length;
  const cuotasPendientes = filas.filter((f) => f.estado === "pendiente").length;

  const capitalPagadoReal = Number(
    filas.reduce((acc, f) => acc + f.capitalPagado, 0).toFixed(2)
  );
  const interesPagadoReal = Number(
    filas.reduce((acc, f) => acc + f.interesPagado, 0).toFixed(2)
  );

  const capitalPendienteTotal = Number(
    Math.max(0, capitalTotal - capitalPagadoReal).toFixed(2)
  );
  const matriculaPagada = Number(
    filas.reduce((acc, f) => acc + f.matriculaPagada, 0).toFixed(2)
  );
  const interesPendienteTotal = Number(
    Math.max(0, interesTotal - interesPagadoReal).toFixed(2)
  );
  const balancePendiente = Number(
    (capitalPendienteTotal + interesPendienteTotal).toFixed(2)
  );

  return {
    ok: true,
    filas,
    resumen: {
      capitalInicial: Number(capital.toFixed(2)),
      interesTotal: Number(interesTotal.toFixed(2)),
      totalPrestamo: Number(totalPrestamo.toFixed(2)),
      cuotaFija: Number(cuotaBase.toFixed(2)),
      plazo,
      frecuencia,

      matricula,
      cuotasMatricula,
      matriculaPagada,

      capitalPagado: capitalPagadoReal,
      interesPagado: interesPagadoReal,

      capitalPendiente: capitalPendienteTotal,
      interesPendiente: interesPendienteTotal,
      balancePendiente,
      balancePendienteOriginal,

      cuotasPagadasCompletas,
      cuotasParciales,
      cuotasPendientes,

      sobranteCapitalNoAplicado: Number(
        Math.max(0, capitalRestantePorAplicar).toFixed(2)
      ),
      sobranteInteresNoAplicado: Number(
        Math.max(0, interesRestantePorAplicar).toFixed(2)
      ),
    },
  };
}

export function formatearMoneda(val) {
  if (!Number.isFinite(val)) return "—";
  return val.toLocaleString("es-HN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

export function formatearFecha(date) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "—";
  return date.toLocaleDateString("es-HN", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}